
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import {getTableName, getSqlColumnRef, isComputedProperty} from '@/worker/db/sqlHelpers';
import {TableItemFormatter} from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import settingx from '@/store/modules/settingx';
import moment from 'moment';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import gdbx from '@/store/modules/gdbx';

@Component({
    components: {
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        IncludeTaxToggle,
        SlPhModuleOnlyToggle,
        TableFilterable,
    },
})
export default class DashboardSalesCustomerRankingTable extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['sales'];
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
    > = ['date'];

    public tableItems: any[] = [];
    public tableFields: any[] = [];

    public get exportFileName() {
        const formattedDate = moment(this.selectedAsOfDate).format('DD MMM YY');
        return 'Sales Customer Ranking' + '_' + formattedDate;
    }

    public get dateFormatted() {
        return this.selectedAsOfDate;
    }

    public get includeTax() {
        return settingx.now.includeTax;
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public get expensiveHook() {
        const { includeTax, selectedAsOfDate, includeCreditNote, includeDebitNote, slPhModuleOnly } = this;
        return JSON.stringify([
            includeTax,
            selectedAsOfDate,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        ]);
    }

    public async expensiveCalc() {
        this.generateLoadingText(0.2, 0.3);

        const dayStart = moment(this.selectedAsOfDate).startOf('day').valueOf();
        const dayEnd = moment(this.selectedAsOfDate).endOf('day').valueOf();

        const amountKey = this.slPhModuleOnly
            ? (this.includeTax ? 'amountWithTax' : 'amount')
            : (this.includeTax ? 'arAmountWithTax' : 'arAmount');
        const profitKey = this.slPhModuleOnly
            ? (this.includeTax ? 'profitIncludeTax' : 'profit')
            : (this.includeTax ? 'arProfitIncludeTax' : 'arProfit');

        const amountCol = getSqlColumnRef('dashboardSales', amountKey);
        const amountExpr = `TRY_CAST(${amountCol} AS DOUBLE)`;
        const profitRef = getSqlColumnRef('dashboardSales', profitKey);
        const profitExpr = isComputedProperty(profitKey) ? profitRef : `TRY_CAST(${profitRef} AS DOUBLE)`;
        const table = getTableName('dashboardSales');

        const jobId = this.currentJobId;
        const queryHead = await this._ensureTablesLoaded(['dashboardSales']);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.5);

        const rows = await queryHead.rawQuery(`
            SELECT "CODE",
                SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN ${amountExpr} ELSE 0 END) as sales_amt,
                SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN ${profitExpr} ELSE 0 END) as sales_profit,
                SUM(CASE WHEN "DOCTYPE" = 'DN' THEN ${amountExpr} ELSE 0 END) as dn_amt,
                SUM(CASE WHEN "DOCTYPE" = 'DN' THEN ${profitExpr} ELSE 0 END) as dn_profit,
                SUM(CASE WHEN "DOCTYPE" = 'CN' THEN ${amountExpr} ELSE 0 END) as cn_amt,
                SUM(CASE WHEN "DOCTYPE" = 'CN' THEN ${profitExpr} ELSE 0 END) as cn_profit
            FROM ${table}
            WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dayStart} AND ${dayEnd}
            GROUP BY "CODE"
        `);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.9);

        this.tableFields = [
            {key: 'customer_name', sortable: true, stickyColumn: true},
            { key: 'total', sortable: true, formatter: TableItemFormatter.currencySplit },
            { key: 'profit', sortable: true, formatter: TableItemFormatter.currencySplit },
        ];

        const result = rows.map((row) => {
            const salesAmt = Number(row.sales_amt) || 0;
            const salesProfit = Number(row.sales_profit) || 0;
            const dnAmt = Number(row.dn_amt) || 0;
            const dnProfit = Number(row.dn_profit) || 0;
            const cnAmt = Number(row.cn_amt) || 0;
            const cnProfit = Number(row.cn_profit) || 0;

            return {
                customer_name: gdbx.customerNames[row.CODE] || row.CODE,
                total: salesAmt
                    + (this.includeDebitNote ? dnAmt : 0)
                    - (this.includeCreditNote ? cnAmt : 0),
                profit: salesProfit
                    + (this.includeDebitNote ? dnProfit : 0)
                    - (this.includeCreditNote ? cnProfit : 0),
            };
        });

        this.tableItems = result.filter((item) => item.total !== 0);

        this.generateLoadingText(1);
        this.saveHistory('tableItems', 'tableFields');
    }
}
